import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../../../shared/api/axiosInstance';

interface ProductsUserSettings {
  id: string | null;
  tenant_id: string | null;
  user_id: string | null;
  default_price_list_id: string | null;
  settings: Record<string, unknown>;
}

interface ProductsTenantSettings {
  id: string | null;
  tenant_id: string | null;
  // Onboarding wizard
  onboarding_completed: boolean;
  sells_physical_products: boolean;
  sells_services_by_project: boolean;
  sells_services_by_time: boolean;
  sells_subscriptions: boolean;
  sells_logistics_services: boolean;
  // Industry verticals
  industry_verticals: string[];
  // Feature toggles
  enable_variants: boolean;
  enable_addons: boolean;
  enable_configurations: boolean;
  enable_bundles: boolean;
  enable_volume_discounts: boolean;
  enable_usage_metering: boolean;
  // Currency settings
  default_currency: string;
  enabled_currencies: string[];
  default_tax_category: string | null;
  // Product creation defaults
  default_base_unit_name: string | null;
  settings: Record<string, unknown>;
  updated_by: string | null;
}

interface ProductsSysadminSettings {
  id: string | null;
  enabled_product_types: string[];
  enabled_billing_cycles: string[];
  /** Hard cap on variants per product, enforced when generating variants. */
  max_variants_per_product: number;
  settings: Record<string, unknown>;
  updated_by: string | null;
}

interface UserSettingsUpdate {
  default_price_list_id?: string | null;
  settings?: Record<string, unknown>;
}

interface TenantSettingsUpdate {
  // Onboarding wizard
  onboarding_completed?: boolean;
  sells_physical_products?: boolean;
  sells_services_by_project?: boolean;
  sells_services_by_time?: boolean;
  sells_subscriptions?: boolean;
  sells_logistics_services?: boolean;
  industry_verticals?: string[];
  // Feature toggles
  enable_variants?: boolean;
  enable_addons?: boolean;
  enable_configurations?: boolean;
  enable_bundles?: boolean;
  enable_volume_discounts?: boolean;
  enable_usage_metering?: boolean;
  // Currency settings
  default_currency?: string;
  enabled_currencies?: string[];
  default_tax_category?: string;
  // Product creation defaults
  default_base_unit_name?: string;
  settings?: Record<string, unknown>;
}

interface SysadminSettingsUpdate {
  enabled_product_types?: string[];
  enabled_billing_cycles?: string[];
  settings?: Record<string, unknown>;
}

export const settingsKeys = {
  all: ['products-settings'] as const,
  user: () => [...settingsKeys.all, 'user'] as const,
  tenant: () => [...settingsKeys.all, 'tenant'] as const,
  sysadmin: () => [...settingsKeys.all, 'sysadmin'] as const,
};

// User Settings

export function useProductsUserSettings() {
  return useQuery({
    queryKey: settingsKeys.user(),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: ProductsUserSettings }>(
        '/api/v1/master-data/products/settings/user'
      );
      return response.data.data;
    },
  });
}

export function useUpdateProductsUserSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UserSettingsUpdate) => {
      const response = await axiosInstance.put<{ data: ProductsUserSettings }>(
        '/api/v1/master-data/products/settings/user',
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.user() });
    },
  });
}

// Tenant Settings

export function useProductsTenantSettings() {
  return useQuery({
    queryKey: settingsKeys.tenant(),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: ProductsTenantSettings }>(
        '/api/v1/master-data/products/settings/tenant'
      );
      return response.data.data;
    },
  });
}

export function useUpdateProductsTenantSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: TenantSettingsUpdate) => {
      const response = await axiosInstance.put<{ data: ProductsTenantSettings }>(
        '/api/v1/master-data/products/settings/tenant',
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.tenant() });
    },
  });
}

// Sysadmin Settings

export function useProductsSysadminSettings() {
  return useQuery({
    queryKey: settingsKeys.sysadmin(),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: ProductsSysadminSettings }>(
        '/api/v1/master-data/products/settings/sysadmin'
      );
      return response.data.data;
    },
  });
}

export function useUpdateProductsSysadminSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: SysadminSettingsUpdate) => {
      const response = await axiosInstance.put<{ data: ProductsSysadminSettings }>(
        '/api/v1/master-data/products/settings/sysadmin',
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.sysadmin() });
    },
  });
}
